/**
 * createShadowHost utility module.
 *
 * Provides a function to create or reuse a shadow root on a host element, injects the Rubik and Roboto Google Fonts,
 * and attaches a compiled stylesheet for encapsulated styling. Returns the ShadowRoot for React rendering.
 *
 * This utility ensures that Wecom components render consistently within Shadow DOM contexts by:
 * 1. Creating or reusing a shadow root on the specified host element
 * 2. Injecting required Google Fonts (Rubik and Roboto)
 * 3. Auto-importing CSS variables from variables.css (single source of truth)
 * 4. Attaching the compiled stylesheet for component styling
 *
 * CSS variables are imported at build time from frontend/src/styles/variables.css
 * via Vite's ?raw import. The :root selector is stripped so only :host applies
 * inside the Shadow DOM. This eliminates manual synchronization.
 */

import variablesCssRaw from "../../../styles/variables.css?raw";

/**
 * CSS variables transformed for Shadow DOM usage.
 * Strips `:root,` from `:root, :host` selectors so variables
 * apply only via `:host` inside the shadow root.
 */
const shadowVariablesCss = variablesCssRaw.replace(/:root,\s*\n:host/g, ":host");

/**
 * Creates (or re-uses) a shadow root on the host element, injects the Rubik and Roboto Google Fonts and the provided stylesheet(s).
 * Ensures fonts and CSS are only injected once per shadow root.
 *
 * @param {string} hostId - The ID of the container <div> to attach the shadow root to.
 * @param {string|string[]} cssHrefs - URL or array of URLs to compiled CSS stylesheets to inject.
 * @returns {ShadowRoot|null} The created or existing ShadowRoot, or null if the host is not found.
 */
export default function createShadowHost(hostId, cssHrefs) {
  const host = document.getElementById(hostId);
  if (!host) return null;

  // Attach (or re-use) the shadow root for style encapsulation
  const shadow = host.shadowRoot || host.attachShadow({ mode: "open" });

  // Inject preconnect hints into the document head (not shadow) for faster font loading
  if (!document.head.querySelector("link[data-wecom-preconnect]")) {
    const pc1 = document.createElement("link");
    pc1.rel = "preconnect";
    pc1.href = "https://fonts.googleapis.com";
    pc1.crossOrigin = "";
    pc1.dataset.wecomPreconnect = "true";
    document.head.appendChild(pc1);

    const pc2 = document.createElement("link");
    pc2.rel = "preconnect";
    pc2.href = "https://fonts.gstatic.com";
    pc2.crossOrigin = "";
    pc2.dataset.wecomPreconnect = "true";
    document.head.appendChild(pc2);
  }

  // Inject Rubik font (only if not already present)
  if (!shadow.querySelector("link[data-wecom-font]")) {
    const font = document.createElement("link");
    font.rel = "stylesheet";
    font.href =
      "https://fonts.googleapis.com/css2?family=Rubik:wght@300;400;500;700&display=swap";
    font.dataset.wecomFont = "true";
    shadow.appendChild(font);
  }

  // Inject Roboto font (only if not already present)
  if (!shadow.querySelector("link[data-wecom-roboto-font]")) {
    const robotoFont = document.createElement("link");
    robotoFont.rel = "stylesheet";
    robotoFont.href =
      "https://fonts.googleapis.com/css2?family=Roboto:wght@400;500&display=swap";
    robotoFont.dataset.wecomRobotoFont = "true";
    shadow.appendChild(robotoFont);
  }

  // Inject Inter font (only if not already present)
  if (!shadow.querySelector("link[data-wecom-inter-font]")) {
    const interFont = document.createElement("link");
    interFont.rel = "stylesheet";
    interFont.href =
      "https://fonts.googleapis.com/css2?family=Inter:ital,wght@0,700&display=swap";
    interFont.dataset.wecomInterFont = "true";
    shadow.appendChild(interFont);
  }

  // Inject CSS variables directly into Shadow DOM (only if not already present)
  // Variables are auto-imported from variables.css at build time — no manual sync needed
  if (!shadow.querySelector("style[data-wecom-variables]")) {
    const variablesStyle = document.createElement("style");
    variablesStyle.dataset.wecomVariables = "true";
    variablesStyle.textContent = shadowVariablesCss;
    shadow.appendChild(variablesStyle);
  }

  // Inject compiled CSS file(s) — supports single string or array of code-split chunks
  const cssArray = Array.isArray(cssHrefs) ? cssHrefs : (cssHrefs ? [cssHrefs] : []);
  for (const href of cssArray) {
    if (href && !shadow.querySelector(`link[data-wecom-css][href="${href}"]`)) {
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = href;
      link.dataset.wecomCss = "true";
      shadow.appendChild(link);
    }
  }

  // Return the ShadowRoot so React can render into it
  return shadow;
}

/**
 * Gets the shadow root portal container for a given host ID.
 * Returns the shadow root if available, otherwise falls back to document.body.
 * Useful for React portals that need to render within the Shadow DOM to maintain CSS encapsulation.
 *
 * @param {string} hostId - The ID of the shadow host container element.
 * @returns {ShadowRoot|HTMLElement} The shadow root if available, otherwise document.body.
 */
export function getShadowRootPortalContainer(hostId) {
  if (typeof document === "undefined") {
    return null;
  }

  const host = document.getElementById(hostId);
  const shadowRoot = host?.shadowRoot;

  // Return shadow root if available, otherwise fall back to document.body
  return shadowRoot || document.body;
}
